import { t } from "@hakili/i18n";
import { Avatar, Button, Card, Field, GuardedButton, useDemandeFonctionnalite } from "@hakili/ui";
import { useCallback, useEffect, useState } from "react";

import { opensControl } from "../shell/rights.js";

import type { DealerBankAccount, Dealership, DoorResult } from "../session/useSession.js";
import { BankAccountBlock } from "./bank-account.js";
import { ProformaDetailsBlock } from "./proforma-details-block.js";

/**
 * `P0-PTA-001`, round I-A: **« Profil » , where this dealership is.**
 *
 * ## Why a dealership needs an address before it needs anything else
 *
 * `I3`'s car form prefills its city and quartier from here, and 6.3.3's
 * expertise is routed by the zone behind them. A dealership with no address is
 * a dealership whose every car has to be told where it is, one at a time, by
 * somebody who will eventually type it differently.
 *
 * ## « Adresse à compléter », in words
 *
 * The four dealerships Hakili has already opened have no address, and the
 * screen says so rather than rendering an empty line (`R55`). An empty line is
 * indistinguishable from a rendering failure; a sentence is a state somebody
 * can act on. `null` and not `""` is what makes the distinction possible, and
 * the door answers `null` deliberately.
 *
 * ## Read by everyone, written by the manager
 *
 * The read carries no right , a person on the forecourt needs the address every
 * car they list is prefilled from. The write is `gererEquipeConcession`, the
 * manager's alone, and **the control is hidden from anyone who does not hold
 * it** exactly as `P0-DLW-007` hides « Équipe »: hiding is a courtesy and the
 * route's own refusal is the enforcement, which still answers in the api's
 * words to anybody who reaches it another way.
 *
 * ## Every refusal is the server's own sentence
 *
 * An uncovered city and an unrecognised Bamako quartier are refused by the
 * door, in French, and rendered verbatim. The screen composes no refusal of its
 * own: it would be a second implementation of a rule `catalog` owns, and the
 * two would disagree the first time the covered cities changed , which they did
 * this very round.
 */

export interface ProfileProps {
  /**
   * `00-SPINE` 6.2's act, passed in , the SAME function the rail's control
   * calls, so a session cannot be ended two different ways.
   */
  readonly deconnecter: () => void;
  readonly readDealership: () => Promise<DoorResult<Dealership>>;
  readonly writeAddress: (input: {
    city: string;
    district: string;
    phone: string;
  }) => Promise<DoorResult<Dealership>>;
  /** `A-DLR` Arc C, `C0`: the manager's write of what his proforma prints (RCCM, NIF, payment terms, warranty). */
  readonly writeProformaDetails: (input: {
    rccm: string;
    nif: string;
    paymentTerms: string;
    warranty: string;
  }) => Promise<DoorResult<Dealership>>;
  /** The rights `/me` answers. The edit control renders only for a holder. */
  readonly droits: Readonly<Record<string, string>>;
  /** `T-2`, M0 (c): the dealership's own bank account, read and entered on this screen. */
  readonly readBankAccount?: () => Promise<DoorResult<DealerBankAccount | null>>;
  readonly writeBankAccount?: (input: {
    bankName: string;
    holderName: string;
    accountNumber: string;
  }) => Promise<DoorResult<DealerBankAccount>>;
}

type Phase = "chargement" | "lecture" | "edition";

export function Profile({
  deconnecter,
  readDealership,
  writeAddress,
  writeProformaDetails,
  droits,
  readBankAccount,
  writeBankAccount,
}: ProfileProps): React.JSX.Element {
  // `OD-18`: the same modal the rail opens, reached from this screen's own
  // address. The provider stands above the whole shell, so this hook resolves
  // and refuses loudly if this screen is ever rendered outside one.
  const { ouvrir: openFeatureRequest } = useDemandeFonctionnalite();
  const [phase, setPhase] = useState<Phase>("chargement");
  const [concession, setConcession] = useState<Dealership | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [ville, setVille] = useState("");
  const [quartier, setQuartier] = useState("");
  const [telephone, setTelephone] = useState("");

  const charger = useCallback(async (): Promise<void> => {
    const answer = await readDealership();
    if (answer.ok) {
      setConcession(answer.value);
      setErreur(null);
    } else {
      setErreur(answer.message);
    }
    setPhase("lecture");
  }, [readDealership]);

  useEffect(() => {
    void charger();
  }, [charger]);

  /**
   * **`P0-DLW-018`, the THIRD place the same defect was living.**
   *
   * This read `droits["gererEquipeConcession"] !== undefined`, and `/me` carries
   * a refused right as `"non"` , so an employee was offered « Modifier », and
   * pressing it would have shown him the door's refusal. That is the founder's
   * own step-6 finding of 5 September, on a different screen, uncured.
   *
   * It survived `F1` because `F1` cured the rail and this file had no control
   * of its own: `P0-PTA-001` shipped a screen with a write and a guard and no
   * spec, so nothing in the unit tier ever rendered it. `profile.spec.tsx` is
   * `F6`'s first act for that reason, and this line is what it found.
   *
   * `opensControl` is the one derivation , 06 §6.1.7's four levels, read in
   * `shell/rights.ts` , so a fourth place cannot get it wrong differently.
   *
   * **The right is not named for what it opens**, and that is filed rather than
   * fixed here: `gererEquipeConcession` is « Voir l'équipe, inviter un
   * collaborateur, le retirer ». The manager is who should edit a dealership's
   * address, so the behaviour is right and the NAME is not , a reader looking
   * for who may change the address finds a row about the team. It binds to a
   * right of its own the day the matrix grows one (`P0-IDN-072`), and
   * `profile.spec.tsx` carries an assertion that fails when it does.
   */
  const peutModifier = opensControl(droits["gererEquipeConcession"]);

  const openEditor = (): void => {
    // The form opens on what is already there, so « Modifier » corrects rather
    // than re-types. Empty when nothing is set, which is the four dealerships'
    // case today.
    setVille(concession?.city ?? "");
    setQuartier(concession?.district ?? "");
    setTelephone(concession?.phone ?? "");
    setErreur(null);
    setPhase("edition");
  };

  const enregistrer = async (): Promise<void> => {
    if (enCours) return;
    setEnCours(true);
    try {
      const answer = await writeAddress({
        city: ville.trim(),
        district: quartier.trim(),
        phone: telephone.trim(),
      });
      if (!answer.ok) {
        // The door's own sentence, rendered. The form STAYS OPEN, holding what
        // was typed: closing it would make the person retype an address to
        // correct one word of it.
        setErreur(answer.message);
        return;
      }
      setConcession(answer.value);
      setErreur(null);
      setPhase("lecture");
    } finally {
      setEnCours(false);
    }
  };

  if (phase === "chargement") {
    return (
      <Card>
        <p className="text-sm text-gray-500" data-testid="profil-chargement">
          {t("common.state.chargement")}
        </p>
      </Card>
    );
  }

  /**
   * The address as one line, or `null` when nobody has set it.
   *
   * Computed once, above the markup, because the narrowing is the readable
   * part: `city` is what decides whether an address EXISTS at all , `I1`'s own
   * rule , and a ternary inlined into a call argument hides which field that
   * decision hangs on.
   */
  const cite = concession?.city ?? null;
  const adresse: string | null = cite === null ? null : `${concession?.district ?? ""} · ${cite}`;

  /** One labelled row of « Coordonnées », or the words for a fact nobody set. */
  const ligne = (label: string, value: string | null, testId: string): React.JSX.Element => (
    <div className="flex items-center justify-between gap-4 py-2.5">
      <span className="text-sm text-gray-500">{label}</span>
      <span
        className={`text-right text-sm ${value === null ? "text-gray-400" : "text-gray-800"}`}
        data-testid={testId}
      >
        {value ?? t("dealer.profil.nonRenseigne")}
      </span>
    </div>
  );

  return (
    <div className="flex flex-col gap-4" data-testid="profil-ecran">
      <h1 className="text-2xl font-semibold tracking-tight">{t("dealer.profil.titre")}</h1>

      {phase === "lecture" ? (
        <>
          {/* The identity card the drawing opens with (`:3871-3882`): the
              dealership's initials, its name, and where it is , « Modifier »
              beside it, because this card IS what that control edits. */}
          <Card>
            <div className="flex items-center gap-4">
              <Avatar name={concession?.name ?? ""} />
              <div className="min-w-0 flex-1">
                <p className="text-base font-semibold text-gray-900" data-testid="profil">
                  {concession?.name ?? ""}
                </p>
                {concession !== null && concession.city !== null ? (
                  <p className="text-sm text-gray-500" data-testid="profil-adresse">
                    {concession.district} · {concession.city}
                  </p>
                ) : (
                  // In WORDS. An empty line reads as a screen that failed to
                  // render; a sentence reads as a thing somebody has not done.
                  <p className="text-sm text-gray-500" data-testid="profil-adresse-absente">
                    {t("dealer.profil.adresseACompleter")}
                  </p>
                )}
              </div>
              {/**
               * `round F5`, the founder's own word on the pair: **« Modifier »
               * is GREYED for an employee, never absent.**
               *
               * The rail hides an ENTRY a right refuses , a screen a person
               * cannot open is furniture. A control on a screen they are
               * standing on is the other case: `I19` says a closed control
               * says why, and an employee who finds no button at all learns
               * that the address cannot be changed, which is false. It can;
               * not by them.
               *
               * `GuardedButton` refuses to compile without the reason, and the
               * door refuses the act regardless of what this screen renders.
               */}
              {peutModifier ? (
                <Button variant="secondary" onClick={openEditor} data-testid="profil-modifier">
                  {t("dealer.profil.modifier")}
                </Button>
              ) : (
                <span data-testid="profil-modifier-refuse">
                  <GuardedButton disabled reason={t("dealer.profil.modifierRefus")}>
                    {t("dealer.profil.modifier")}
                  </GuardedButton>
                </span>
              )}
            </div>
            <p className="mt-3 text-sm text-gray-500">{t("dealer.profil.intro")}</p>
          </Card>

          {/* « Coordonnées » , the drawing's five rows, of which this door
              answers two. The other three are named at the foot rather than
              rendered as blanks a manager would try to fill in. */}
          <Card>
            <h2 className="text-sm font-semibold">{t("dealer.profil.coordonnees")}</h2>
            <div className="mt-2 divide-y divide-gray-50">
              {/* `round F5`, to the pair: the drawing's own order , the
                  telephone above the address (`hakili-dealer.jsx`, « Profil »).
                  Both are the dealership's, and the one a person reads for is
                  the number. */}
              {ligne(t("dealer.profil.telephoneLigne"), concession?.phone ?? null, "profil-telephone")}
              {ligne(t("dealer.profil.adresse"), adresse, "profil-ligne-adresse")}
            </div>
            <p className="mt-3 text-xs text-gray-400" data-testid="profil-en-attente">
              {t("dealer.profil.enAttente")}
            </p>
          </Card>

          {/* `A-DLR` Arc C, `C0`: what the proforma prints of the dealership, entered here by its manager. */}
          <ProformaDetailsBlock
            dealership={concession}
            canWrite={peutModifier}
            write={writeProformaDetails}
            onSaved={setConcession}
          />

          {/* `T-2`, M0 (c): the beneficiary of the 90 %, the dealer's own, entered here and never by Hakili. */}
          {readBankAccount !== undefined && writeBankAccount !== undefined ? (
            <BankAccountBlock
              readBankAccount={readBankAccount}
              writeBankAccount={writeBankAccount}
              canWrite={peutModifier}
            />
          ) : null}

          {/**
           * The drawing's own « Une idée » block. It is here AND in the rail
           * because the drawing puts it in both, and a dealership looking for
           * how to reach Hakili looks at its profile.
           *
           * `OD-18`, closed at `round F5`: it was inert with its reason, and
           * the reason , no route received a demand from a dealership's space ,
           * is gone. Same modal as the rail's, opened at this screen's own
           * address, so a demand left here is read back to « Profil ».
           */}
          <Card>
            {/* The drawing puts the control on the RIGHT of the two lines, on
                their own row , the same shape « Sécurité » below it already
                has. Two cards with one layout read as one panel with two
                subjects, which is what they are. */}
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold">{t("dealer.profil.idee")}</h2>
                <p className="mt-0.5 text-xs text-gray-500" data-testid="profil-idee-intro">
                  {t("dealer.profil.ideeIntro")}
                </p>
              </div>
              <Button
                variant="secondary"
                onClick={() => {
                  openFeatureRequest("concessionnaire:profil:idee");
                }}
                data-testid="profil-idee"
              >
                {t("dealer.profil.ideeBouton")}
              </Button>
            </div>
          </Card>

          {/**
           * « Sécurité », with the way out ON it, as the drawing has it
           * (`hakili-dealer.jsx:3918-3929`).
           *
           * **Two exits are not a duplication.** `00-SPINE` 6.2 puts one in
           * the chrome so that every surface can be left from wherever a
           * person is standing; the drawing puts a second here, under the
           * word « Sécurité », because that is where somebody GOES to end a
           * session deliberately rather than to move on from a screen. They
           * call the same `deconnecter`, so there is one act and two doors to
           * it , and the sentence beside it says what that act does, which is
           * the half a person needs before pressing either.
           *
           * The drawing's own « Dernière connexion aujourd'hui à 08:20 » is
           * not rendered: no door carries a last login, and inventing a time
           * on a SECURITY panel is the worst place in the product to invent
           * one (`R81`).
           */}
          <Card>
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold">{t("dealer.profil.securite")}</h2>
                <p className="mt-0.5 text-xs text-gray-500" data-testid="profil-securite">
                  {t("dealer.profil.securiteIntro")}
                </p>
              </div>
              <Button variant="secondary" onClick={deconnecter} data-testid="profil-deconnexion">
                {t("dealer.session.deconnexion")}
              </Button>
            </div>
          </Card>
        </>
      ) : (
        <Card>
          <div className="space-y-3" data-testid="profil-formulaire">
            <Field label={t("dealer.profil.ville")} htmlFor="profil-ville">
              <input
                id="profil-ville"
                value={ville}
                onChange={(event) => {
                  setVille(event.target.value);
                }}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                data-testid="profil-ville"
              />
            </Field>
            <Field label={t("dealer.profil.quartier")} htmlFor="profil-quartier">
              <input
                id="profil-quartier"
                value={quartier}
                onChange={(event) => {
                  setQuartier(event.target.value);
                }}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                data-testid="profil-quartier"
              />
            </Field>
            <Field label={t("dealer.profil.telephone")} htmlFor="profil-telephone-champ">
              <input
                id="profil-telephone-champ"
                value={telephone}
                onChange={(event) => {
                  setTelephone(event.target.value);
                }}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
                data-testid="profil-telephone-champ"
              />
            </Field>
            <div className="flex items-center gap-2 pt-1">
              {/* No `disabled`: `ButtonProps` has none, and `inactive` is
                WF-CLI-01's client-only shape. A double submit is refused by
                `enregistrer`'s own `if (enCours) return`, which is the guard
                that actually holds , a disabled attribute is a courtesy a
                second click can outrun. */}
              <Button
                onClick={() => {
                  void enregistrer();
                }}
                data-testid="profil-enregistrer"
              >
                {t("common.action.enregistrer")}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setErreur(null);
                  setPhase("lecture");
                }}
                data-testid="profil-annuler"
              >
                {t("common.action.annuler")}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {erreur === null ? null : (
        <p role="alert" className="text-sm text-rose-600" data-testid="profil-erreur">
          {erreur}
        </p>
      )}
    </div>
  );
}
